import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Mail, Plus, Loader2, Users, Eye, Send, Pencil, Trash2, CheckCircle2, Clock, FileEdit, MousePointerClick, Copy,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  draft: { label: 'Draft', icon: FileEdit, cls: 'bg-gray-100 text-gray-600' },
  scheduled: { label: 'Scheduled', icon: Clock, cls: 'bg-blue-100 text-blue-700' },
  sending: { label: 'Sending', icon: Loader2, cls: 'bg-amber-100 text-amber-700', spin: true },
  sent: { label: 'Sent', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Failed', icon: Clock, cls: 'bg-red-100 text-red-700' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.draft;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>
      <Icon className={`w-3 h-3 ${s.spin ? 'animate-spin' : ''}`} />{s.label}
    </span>
  );
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-campaigns`);
      setCampaigns(res.data.campaigns || []);
    } catch {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    const t = setInterval(fetchCampaigns, 15000); // refresh for live sending stats
    return () => clearInterval(t);
  }, [fetchCampaigns]);

  const createCampaign = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/email-campaigns`, { name: 'Untitled campaign' });
      navigate(`/admin/campaigns/${res.data.id}`);
    } catch {
      toast.error('Could not create campaign');
      setCreating(false);
    }
  };

  const duplicateCampaign = async (c, e) => {
    e.stopPropagation();
    try {
      const res = await axios.post(`${API}/email-campaigns/${c.id}/duplicate`);
      toast.success('Campaign duplicated - opening the copy');
      navigate(`/admin/campaigns/${res.data.id}`);
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/email-campaigns/${deleteTarget.id}`);
      toast.success('Campaign deleted');
      setDeleteTarget(null);
      fetchCampaigns();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const openRate = (c) => (c.sent_count ? Math.round((c.opened_count || 0) / c.sent_count * 100) : 0);
  const clickRate = (c) => (c.sent_count ? Math.round((c.clicked_count || 0) / c.sent_count * 100) : 0);

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
              <Mail className="w-5 h-5 text-white" />
            </span>
            Campaigns
          </h1>
          <p className="text-gray-500 mt-2">Send HTML marketing emails to a contact list. AI-drafted, personalized, with one-click unsubscribe.</p>
        </div>
        <Button onClick={createCampaign} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg" data-testid="new-campaign-btn">
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} New campaign
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-200" data-testid="campaigns-empty">
          <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">No campaigns yet</h3>
          <p className="text-gray-500 mt-1 mb-5">Create your first marketing email and send it to a list.</p>
          <Button onClick={createCampaign} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> New campaign
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            <div className="col-span-5">Campaign</div>
            <div className="col-span-2">List</div>
            <div className="col-span-1 text-right">Recipients</div>
            <div className="col-span-1 text-right">Sent</div>
            <div className="col-span-2 text-right">Open / Click</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          <div className="divide-y divide-gray-100">
            {campaigns.map((c) => (
              <div key={c.id} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-gray-50/60 transition-colors cursor-pointer"
                onClick={() => navigate(`/admin/campaigns/${c.id}`)} data-testid={`campaign-row-${c.id}`}>
                <div className="col-span-5 min-w-0 pr-4">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">{c.name}</p>
                    <StatusBadge status={c.status} />
                  </div>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{c.subject || 'No subject yet'}</p>
                </div>
                <div className="col-span-2 text-sm text-gray-600 truncate flex items-center gap-1">
                  {c.list_name ? <><Users className="w-3.5 h-3.5 text-gray-400" />{c.list_name}</> : <span className="text-gray-300">-</span>}
                </div>
                <div className="col-span-1 text-right text-sm text-gray-700">{c.total_recipients || 0}</div>
                <div className="col-span-1 text-right text-sm text-gray-700">{c.sent_count || 0}</div>
                <div className="col-span-2 text-right">
                  <span className="inline-flex items-center gap-1 text-sm text-gray-700" title="Open rate">
                    <Eye className="w-3.5 h-3.5 text-gray-400" /> {openRate(c)}%
                    <span className="text-gray-400 text-xs">({c.opened_count || 0})</span>
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm text-gray-700 ml-3" title="Click rate" data-testid={`campaign-clicks-${c.id}`}>
                    <MousePointerClick className="w-3.5 h-3.5 text-gray-400" /> {clickRate(c)}%
                    <span className="text-gray-400 text-xs">({c.clicked_count || 0})</span>
                  </span>
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-blue-600" onClick={() => navigate(`/admin/campaigns/${c.id}`)} title="Edit" data-testid={`edit-campaign-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-violet-600" onClick={(e) => duplicateCampaign(c, e)} title="Duplicate" data-testid={`duplicate-campaign-${c.id}`}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-600" onClick={() => setDeleteTarget(c)} title="Delete" data-testid={`delete-campaign-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete campaign?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteTarget?.name}" and its send history will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleDelete(); }} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-campaign">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Campaigns;
